import { useEffect, useState, useMemo } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, LogIn, LogOut, Trash2, Wine, Activity, ShoppingBag, CreditCard, X, FileText, Mail, Users, Pencil } from "lucide-react";
import {
  getReservation, deleteConsumption, deletePayment, checkIn, checkOut, deleteReservation,
} from "@/lib/api";
import { fmt, fmtDate } from "@/lib/api";
import AddConsumptionDialog from "./AddConsumptionDialog";
import AddPaymentDialog from "./AddPaymentDialog";
import EditReservationDialog from "./EditReservationDialog";

const STATUS_STYLES = {
  booked: "bg-slate-100 text-slate-700 border-slate-200",
  checked_in: "bg-blue-100 text-blue-700 border-blue-200",
  checked_out: "bg-slate-200 text-slate-600 border-slate-300",
  cancelled: "bg-red-100 text-red-700 border-red-200",
};

const PAY_STYLES = {
  paid: "bg-emerald-100 text-emerald-700 border-emerald-200",
  partial: "bg-amber-100 text-amber-700 border-amber-200",
  unpaid: "bg-red-100 text-red-700 border-red-200",
};

const CAT_ICON = { bar: Wine, activities: Activity, shop: ShoppingBag };

export default function ReservationDrawer({ reservationId, open, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [consumpOpen, setConsumpOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const load = async () => {
    if (!reservationId) return;
    setLoading(true);
    try {
      const r = await getReservation(reservationId);
      setData(r);
    } catch (e) {
      toast.error("Failed to load reservation");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && reservationId) load();
  }, [open, reservationId]);

  const refresh = async () => {
    await load();
    onChanged?.();
  };

  const handleCheckIn = async () => {
    await checkIn(reservationId);
    toast.success("Checked in");
    refresh();
  };
  const handleCheckOut = async () => {
    await checkOut(reservationId);
    toast.success("Checked out");
    refresh();
  };

  const handleDeleteReservation = async () => {
    try {
      await deleteReservation(reservationId);
      toast.success("Reservation deleted");
      setConfirmDelete(false);
      onClose();
      onChanged?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to delete reservation";
      toast.error(typeof msg === "string" ? msg : "Failed to delete reservation");
    }
  };

  const removeConsumption = async (id) => {
    await deleteConsumption(id);
    refresh();
  };
  const removePayment = async (id) => {
    await deletePayment(id);
    refresh();
  };

  const s = data?.summary;
  const remainingColor = s && s.remaining > 0.01 ? "text-red-600" : "text-emerald-600";

  const groupedConsumptions = useMemo(() => {
    if (!data?.consumptions) return {};
    return data.consumptions.reduce((acc, c) => {
      (acc[c.category] = acc[c.category] || []).push(c);
      return acc;
    }, {});
  }, [data]);

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-[560px] p-0 flex flex-col"
          data-testid="reservation-drawer"
        >
          {/* Sticky Header */}
          <SheetHeader className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 z-10">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <SheetTitle className="font-heading text-xl text-slate-900 truncate">
                  {data?.guest_name || "Loading..."}
                </SheetTitle>
                {data?.guest_email && (
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1 truncate">
                    <Mail size={11} /> {data.guest_email}
                  </div>
                )}
                <div className="text-sm text-slate-500 mt-1 truncate">
                  {data?.unit?.display_name || "—"}
                </div>
                <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
                  <span>{fmtDate(data?.check_in)} → {fmtDate(data?.check_out)}</span>
                  {data && (
                    <span className="inline-flex items-center gap-1 text-slate-500" data-testid="drawer-guest-count">
                      <Users size={10} strokeWidth={2} />
                      {1 + (data.extra_guests || 0)} guest{(1 + (data.extra_guests || 0)) === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                {data && (
                  <>
                    <Badge variant="outline" className={STATUS_STYLES[data.status]} data-testid="drawer-status-badge">
                      {data.status.replace("_", " ")}
                    </Badge>
                    {s && (
                      <Badge variant="outline" className={PAY_STYLES[s.payment_status]}>
                        {s.payment_status}
                      </Badge>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditOpen(true)}
                      className="h-8 px-2.5 text-xs border-slate-200"
                      data-testid="drawer-edit-reservation"
                    >
                      <Pencil size={12} /> Edit
                    </Button>
                  </>
                )}
              </div>
            </div>
          </SheetHeader>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {loading && <div className="text-sm text-slate-400">Loading...</div>}

            {/* Financial cards */}
            {s && (
              <div className="grid grid-cols-2 gap-3">
                <Card className="p-4 border-slate-200 shadow-none">
                  <div className="text-xs uppercase tracking-wider text-slate-500">Accommodation</div>
                  <div className="font-heading text-xl font-bold text-slate-900 mt-1">{fmt(s.accommodation_total)}</div>
                </Card>
                <Card className="p-4 border-slate-200 shadow-none">
                  <div className="text-xs uppercase tracking-wider text-slate-500">Consumptions</div>
                  <div className="font-heading text-xl font-bold text-slate-900 mt-1">{fmt(s.consumptions_total)}</div>
                </Card>
                <Card className="p-4 border-slate-200 shadow-none">
                  <div className="text-xs uppercase tracking-wider text-slate-500">Total</div>
                  <div className="font-heading text-xl font-bold text-slate-900 mt-1" data-testid="drawer-total">{fmt(s.total)}</div>
                </Card>
                <Card className="p-4 border-slate-200 shadow-none">
                  <div className="text-xs uppercase tracking-wider text-slate-500">Paid</div>
                  <div className="font-heading text-xl font-bold text-emerald-600 mt-1">{fmt(s.paid)}</div>
                </Card>
                <Card className="p-4 col-span-2 border-slate-200 shadow-none bg-slate-50">
                  <div className="text-xs uppercase tracking-wider text-slate-500">Remaining</div>
                  <div className={`font-heading text-3xl font-bold mt-1 ${remainingColor}`} data-testid="drawer-remaining">
                    {fmt(s.remaining)}
                  </div>
                </Card>
              </div>
            )}

            {/* Consumptions */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-semibold text-slate-900">Consumptions</h3>
                <div className="text-xs text-slate-500">{data?.consumptions?.length || 0} items</div>
              </div>
              {data?.consumptions?.length > 0 ? (
                <div className="border border-slate-200 rounded-md overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="h-9">Item</TableHead>
                        <TableHead className="h-9 w-14 text-center">Qty</TableHead>
                        <TableHead className="h-9 w-20 text-right">Total</TableHead>
                        <TableHead className="h-9 w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(groupedConsumptions).map(([cat, items]) =>
                        items.map((c) => {
                          const Icon = CAT_ICON[cat] || Wine;
                          return (
                            <TableRow key={c.id} className="hover:bg-slate-50">
                              <TableCell className="py-2">
                                <div className="flex items-center gap-2">
                                  <Icon size={14} className="text-slate-400" strokeWidth={1.5} />
                                  <span className="text-sm">{c.product_name}</span>
                                </div>
                              </TableCell>
                              <TableCell className="py-2 text-center text-sm">{c.quantity}</TableCell>
                              <TableCell className="py-2 text-right font-medium text-sm">{fmt(c.total)}</TableCell>
                              <TableCell className="py-2 text-right">
                                <button
                                  onClick={() => removeConsumption(c.id)}
                                  className="text-slate-300 hover:text-red-500 p-1"
                                  data-testid={`remove-consumption-${c.id}`}
                                >
                                  <X size={14} />
                                </button>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-sm text-slate-400 border border-dashed border-slate-200 rounded-md p-6 text-center">
                  No consumptions yet
                </div>
              )}
            </div>

            {/* Payments */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-semibold text-slate-900">Payments</h3>
                <div className="text-xs text-slate-500">{data?.payments?.length || 0} entries</div>
              </div>
              {data?.payments?.length > 0 ? (
                <div className="space-y-2">
                  {data.payments.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between border border-slate-200 rounded-md px-3 py-2.5 bg-white"
                    >
                      <div className="flex items-center gap-3">
                        <CreditCard size={16} className="text-slate-400" strokeWidth={1.5} />
                        <div>
                          <div className="text-sm font-medium text-slate-900">{fmt(p.amount)}</div>
                          <div className="text-xs text-slate-500 capitalize">{p.method} · {fmtDate(p.date)}</div>
                        </div>
                      </div>
                      <button
                        onClick={() => removePayment(p.id)}
                        className="text-slate-300 hover:text-red-500 p-1"
                        data-testid={`remove-payment-${p.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-slate-400 border border-dashed border-slate-200 rounded-md p-6 text-center">
                  No payments recorded
                </div>
              )}
            </div>

            {data?.extra_guest_names?.length > 0 && (
              <>
                <Separator />
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                    <Users size={11} /> Additional guests
                  </div>
                  <div className="flex flex-wrap gap-1.5" data-testid="drawer-extra-names">
                    {data.extra_guest_names.map((n, i) => (
                      <span key={i} className="px-2 py-1 rounded bg-slate-100 text-slate-700 text-xs">{n}</span>
                    ))}
                  </div>
                </div>
              </>
            )}

            {data?.notes && (
              <>
                <Separator />
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Notes</div>
                  <div className="text-sm text-slate-700">{data.notes}</div>
                </div>
              </>
            )}

            <button
              onClick={() => setConfirmDelete(true)}
              className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
              data-testid="delete-reservation-btn"
            >
              <Trash2 size={12} /> Delete reservation
            </button>
          </div>

          {/* Sticky footer with quick actions */}
          <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={() => setConsumpOpen(true)}
                className="h-11 bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="drawer-add-consumption"
              >
                <Plus size={16} /> Consumption
              </Button>
              <Button
                onClick={() => setPaymentOpen(true)}
                variant="outline"
                className="h-11 border-slate-300"
                data-testid="drawer-add-payment"
              >
                <CreditCard size={16} /> Payment
              </Button>
            </div>
            <div className="grid grid-cols-4 gap-2">
              <Button
                onClick={handleCheckIn}
                variant="outline"
                disabled={data?.status === "checked_in" || data?.status === "checked_out"}
                className="h-10 border-slate-300"
                data-testid="drawer-check-in"
              >
                <LogIn size={16} /> In
              </Button>
              <Button
                onClick={handleCheckOut}
                variant="outline"
                disabled={data?.status !== "checked_in"}
                className="h-10 border-slate-300"
                data-testid="drawer-check-out"
              >
                <LogOut size={16} /> Out
              </Button>
              <Button
                onClick={() => window.open(`/bill/${reservationId}`, "_blank")}
                variant="outline"
                disabled={!data}
                className="h-10 border-slate-300"
                data-testid="drawer-view-bill"
              >
                <FileText size={16} /> Account Summary
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {reservationId && (
        <>
          <AddConsumptionDialog
            reservationId={reservationId}
            open={consumpOpen}
            onClose={() => setConsumpOpen(false)}
            onSaved={refresh}
          />
          <AddPaymentDialog
            reservationId={reservationId}
            open={paymentOpen}
            onClose={() => setPaymentOpen(false)}
            onSaved={refresh}
            defaultRemaining={s?.remaining}
          />
          <EditReservationDialog
            reservation={data}
            open={editOpen}
            onClose={() => setEditOpen(false)}
            onSaved={() => { refresh(); onChanged?.(); }}
          />
          <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
            <AlertDialogContent data-testid="delete-reservation-confirm">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this reservation?</AlertDialogTitle>
                <AlertDialogDescription>
                  {data?.guest_name ? `${data.guest_name} · ` : ""}This also removes all consumptions and payments for this reservation. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteReservation}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="delete-reservation-confirm-btn"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </>
  );
}
